import React, {
  useEffect, useRef, useCallback, useState, useMemo,
} from 'react';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Link,
} from 'react-router-dom';
import useTheme from 'theme/useTheme';
import {
  Segment, Divider, Icon, Loader, Header, Message, Button, Popup, Label,
} from 'semantic-ui-react';
import {
  useAppSelector,
} from 'store';

import { IdeaAssessmentConflictOfInterestBox } from 'components/ideas/Assessment';
import IPStatementInfo from 'components/lib/IPStatementInfo';
import { SubmitIdeaButton } from 'components/lib/Ideas';
import { AssessmentClosureInfo } from 'components/challenges/Challenge/Assessments/YourAssessments';
import util from 'utils/utils';
import IdeaBusinessProfile from '../Components/IdeaBusinessProfile';
import Concept from './Concept';
import Customers from './Customers';
import Competitors from './Competitors';
import Compatibility from './Compatibility';
import Contents from './Contents';
import Cash from './Cash';
import Custom from './Custom';
import FreeformTemplate from './Freeform';

import IdeaAttachments from '../Attachments';
import IdeaComments from '../Comments';
import IdeaAssessment from '../Assessment';
import IdeaNameField from './Name';
import IdeaSectionHeader from '../SectionHeader.jsx';
import { Other, OpenAPI } from 'simplydo/interfaces';
import { Sticky } from 'components/lib/UI';
import useIdeaScrollTracking from '../useIdeaScrollTracking';

export const includeSections = {
  concept: {
    key: 'concept', title: 'Concept', description: 'A short, sharp description of your idea!', video: 'concept.mp4', colour: '#f4cde2',
  },
  customers: {
    key: 'customers', title: 'Customers', description: 'Identify your target customers and potential market.', video: 'customers.mp4', colour: '#b4e6f4',
  },
  competitors: {
    key: 'competitors', title: 'Competitors', description: 'Identify the competition and find your gap.', video: 'competitors.mp4', colour: '#f5ccb1',
  },
  compatibility: {
    key: 'compatibility', title: 'Compatibility', description: 'Are you the right individual or team to make this idea happen?', video: 'compatibility.mp4', colour: '#cdc4de',
  },
  contents: {
    key: 'contents', title: 'Contents', description: 'This is the shopping list of essential ingredients you need to make your idea happen.', video: 'contents.mp4', colour: '#fae7bd',
  },
  cash: {
    key: 'cash', title: 'Cash', description: 'Understanding the money coming in and going out.', video: 'cash.mp4', colour: '#cae6c9',
  },
};

export class IncludeSection extends React.Component<{
  type: string,
  idea: Other.IIdea,
  isEditing?: boolean,
  updateIdea?: (idea: Other.IIdea) => void,
  externalChanges?: any,
}> {
  section: any;
  constructor(props) {
    super(props);

    this.section = includeSections[props.type];
  }

  render() {
    const {
      type, idea, isEditing, updateIdea, externalChanges,
    } = this.props;
    const sectionProps = { isEditing, updateIdea, externalChanges };
    const ideaId = idea._id;

    switch (type) {
      case 'concept': return <Concept ideaId={ideaId} conceptSupportingImage={idea.conceptSupportingImage} conceptRoughIdea={idea.conceptRoughIdea} conceptVideo={idea.conceptVideo} conceptKeywords={idea.conceptKeywords} key={type} includeSection={this.section} {...sectionProps} />;
      case 'customers': return <Customers key={type} ideaId={ideaId} customersPeople={idea.customersPeople} includeSection={this.section} customersStrategy={idea.customersStrategy} customersMarket={idea.customersMarket} {...sectionProps} />;
      case 'competitors': return <Competitors competitors={idea.competitors} competitorsStrategy={idea.competitorsStrategy} ideaId={ideaId} key={type} includeSection={this.section} {...sectionProps} />;
      case 'compatibility': return <Compatibility compatibilityPeople={idea.compatibilityPeople} ideaId={ideaId} compatibilityPassion={idea.compatibilityPassion} key={type} includeSection={this.section} compatibilityBelief={idea.compatibilityBelief} compatibilityHistory={idea.compatibilityHistory} compatibilityRisk={idea.compatibilityRisk} compatibilityNetwork={idea.compatibilityNetwork} {...sectionProps} />;
      case 'contents': return <Contents contentsOverview={idea.contentsOverview} contentsWhat={idea.contentsWhat} contentsWho={idea.contentsWho} contentsWhere={idea.contentsWhere} ideaId={ideaId} key={type} contentsAdditionalInfo={idea.contentsAdditionalInfo} includeSection={this.section} {...sectionProps} />;
      case 'cash': return <Cash cashFunding={idea.cashFunding} cashFundingNotes={idea.cashFundingNotes} cashMoneyTable={idea.cashMoneyTable} cashMoneyOut={idea.cashMoneyOut} cashMoneyIn={idea.cashMoneyIn} ideaId={ideaId} key={type} includeSection={this.section} {...sectionProps} />;
      default: return null;
    }
  }
}

export const IdeaSection = styled.div<{ ispreview?: boolean, include?: string }>`
  border-radius:5px;
  box-shadow: ${(props) => ((props.ispreview) ? null : '0px 10px 10px rgba(0,0,0,0.1)')};
  ${(props) => ((props.include && !props.ispreview) ? `border: 4px solid ${includeSections[props.include].colour};` : '')}
  @media print {
    page-break-after: always;
    margin: 2cm;
  }
`;

export const IdeaSectionBody = styled(Segment)<{ ispreview?: boolean }>`
  border: ${(props) => (props.ispreview ? 'none !important' : 'none')};
`;

const MobileSubmitButtonContainer = styled.div<{ addMargin?: boolean, isSubmitted?: boolean, disabled?: boolean }>`
  margin: ${({ addMargin }) => (addMargin ? '-10px 0 20px' : '-10px 0')};
  border-radius: 10px;
  background-color: ${({ isSubmitted, theme }) => ((!isSubmitted) ? '#21BA45' : theme.primaryColour)};
  box-shadow: 0px 10px 10px rgba(0,0,0,0.1);
  &:hover {
    opacity: ${({ disabled }) => (!disabled && '90%')};
  }
  color: white;
  font-weight: bold;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-self: stretch;
  align-items: center;
  padding: 10px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (!disabled ? 'pointer' : 'default')};
  span {
    display: block;
    font-size: 16px;
    margin-right: 5px;
  }
  i {
    font-size: 1.2em;
    margin-bottom: 3px;
  }
  .submit-button-loader {
    margin: 0 3px !important;
  }
`;

const MobileSubmitButton = withTranslation()(({
  t,
  loading,
  onClick,
  unsubmit,
  addMargin,
  ownerChallenge,
  idea,
  handleKeyDown: propHandleKeyDown,
  buttonRef,
  tabIndex,
  ...rest
}) => {
  const disabled = ownerChallenge?.stage === 'closed';
  const theme = useTheme();

  const handleKeyDown = useCallback((event) => {
    if (propHandleKeyDown) propHandleKeyDown(event);
    if (event.key === 'Enter' && !disabled) {
      onClick();
    }
  }, [disabled, onClick, propHandleKeyDown]);

  return (
    <MobileSubmitButtonContainer
      isSubmitted={idea?.isSubmitted}
      disabled={disabled}
      onClick={!disabled && onClick}
      addMargin={addMargin}
      onKeyDown={(event) => handleKeyDown(event)}
      ref={buttonRef}
      tabIndex={tabIndex}
      {...rest}
    >
      {loading
        ? (
          <Loader active inverted className='submit-button-loader' inline='centered' size='tiny' />
        ) : (
          <>
            {!unsubmit
              ? (
                <Icon name='paper plane' />
              ) : (
                <Icon name='edit' style={{ fontSize: theme.sizes.isMobile ? '0.9em' : '1em', height: '19px' }} />
              )
            }
          </>
        )
      }
      {!unsubmit
        ? (
          <span>
            {t('generic.submit')}
          </span>
        ) : (
          <span style={{ fontSize: theme.sizes.isMobile ? '0.9em' : '1em' }}>
            {t('generic.unsubmitAndResume')}
          </span>
        )
      }
    </MobileSubmitButtonContainer>
  );
});


export const IdeaBodySection = (props) => {
  const {
    section,
    sectionIndex,
    isPreview,
    externalChanges,
    idea,
    setRef,
    updateIdea,
    isEditing,
    noShadow,
  } = props;
  if (!section) return null;
  return (
    <>
      <IdeaSection ispreview={isPreview} include={section.include} id={section.include ? section.include : section.name} ref={(ref) => setRef && setRef(ref, section.id)}>
        <IdeaSectionHeader isPreview={isPreview} sectionInclude={includeSections[section.include]} section={section} sectionIndex={sectionIndex} {...props} />
        <IdeaSectionBody ispreview={isPreview} raised attached>
          {section.type === 'businessProfile'
            ? (
              <IdeaBusinessProfile
                idea={idea}
                isEditing={isEditing}
                updateIdea={updateIdea}
              />
            ) : (
              section.include
                ? <IncludeSection type={section.include} externalChanges={externalChanges || {}} idea={idea} updateIdea={updateIdea} isEditing={isEditing} />
                : <Custom {...props} section={section} />
            )
          }
        </IdeaSectionBody>

      </IdeaSection>
      <IdeaAssessment section={section} noShadow={noShadow} {...props} />
    </>
  );
};

export const TextFields = ['shorttext', 'longtext', 'richtext'];

const ChildIdeaChooser = styled.div`
  z-index: 999;
  padding: 5px;
  border-radius: 5px;
  background-color: white;
  box-shadow: 0px 10px 10px rgba(0,0,0,0.1);
  border: 0.5px solid #e9ebee;
  > .title {
    opacity: 0.8;
    display: block;
    margin: 0 5px;
    font-weight: bold;
    margin-bottom: 5px;
  }
  > .buttons {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    gap: 5px;
    .ui.button.merge-button-tab {
      width: 250px;
    }
    .button-idea-name {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const StickyMergeIdeaHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;
  align-items: center;
  .jump-to-section-button {
    margin-top: 5px;
    height: 25px;
    padding: 5px 12.5px;
  }
  ${ChildIdeaChooser} {
    width: 100%;
  }
`;

const BodyContainer = ({
  idea,
  divRef,
  user,
  removeIdeaFromMerge,
  removingIdeaFromMerge,
  setRef,
  sectionRefs,
  ...rest
}) => {
  /*
    Code for handling merged idea bodies

    One idea is set as the "active" idea, and the content from that idea will be what is shown
  */

  const {
    activeItemId,
    // setActiveItemId,
    scrollToSection,
    // @ts-ignore
  } = useIdeaScrollTracking(idea._id, sectionRefs, divRef, 210);
  /* */

  const usingMergedIdea = useMemo(() => !!idea?.children?.length, [idea.children]);
  const numberOfChildren = useMemo(() => idea?.ownerChildren?.length ?? 0, [idea.ownerChildren]);
  const [activeChildIdea, setActiveChildIdea] = useState<Other.IIdea|null>(null);
  const [prevSectionViewed, setPrevSectionViewed] = useState<string>('');

  useEffect(() => {
    if (numberOfChildren > 0 && !activeChildIdea) {
      setActiveChildIdea(idea.ownerChildren[0]);
    }
  }, [activeChildIdea, idea.ownerChildren, numberOfChildren]);

  const changeActiveChildIdea = useCallback((childIdea) => {
    if (divRef?.current) {
      divRef.current.scrollTop = 0;
    } else {
      window.scrollTo(0, 0);
    }
    setPrevSectionViewed(activeItemId);
    setActiveChildIdea(childIdea);
  }, [activeItemId, divRef]);

  // You can jump to the section that you were viewing in the previous idea, if the section exists in the new idea, and it's not the first section
  const canJumpToSectionInIdea = useMemo(() => {
    if (activeChildIdea === null || prevSectionViewed === '') {
      return false;
    }
    const sectionIds = Object.keys(sectionRefs.current);
    const prevSectionIndex = sectionIds.findIndex((sectionId) => sectionId === prevSectionViewed);
    return (
      (sectionIds.length > 0)
      && (sectionIds.indexOf(prevSectionViewed) !== -1)
      && (prevSectionIndex !== 0)
    )
  }, [activeChildIdea, prevSectionViewed, sectionRefs]);

  const onScrollToPrevSection = useCallback((sectionId) => {
    setPrevSectionViewed('');
    scrollToSection(sectionId);
  }, [scrollToSection]);

  const usingIdea = useMemo(() => usingMergedIdea ? activeChildIdea : idea, [usingMergedIdea, activeChildIdea, idea])

  const prevSectionName = useMemo(() => activeChildIdea?.ideaTemplate?.body?.find((section) => section.id === prevSectionViewed)?.name, [activeChildIdea?.ideaTemplate?.body, prevSectionViewed]);

  if (usingMergedIdea) {
    if (activeChildIdea === null) {
      return null;
    }
    return (
      <div>
        <Sticky
          top={window.location.pathname === `/ideas/${idea._id}` ? 110 : 5}
          bottom={undefined}
          style={{ zIndex: 100 }}
        >
          <StickyMergeIdeaHeader>
            <ChildIdeaChooser>
              <span className='title'>Viewing merged idea...</span>
              <div className='buttons'>
                {idea.ownerChildren.map((childIdea) => (
                  <Button.Group>
                    <Button
                      active={childIdea._id === activeChildIdea?._id}
                      key={childIdea._id}
                      onClick={() => changeActiveChildIdea(childIdea)}
                      circular
                      className='merge-button-tab'
                    >
                      <Button.Content>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span className='button-idea-name'>
                            {childIdea.name}
                          </span>
                          {childIdea?.owner
                            ? (
                              <span style={{ opacity: 0.5, fontSize: '0.8em' }}>By {childIdea?.owner?.profile?.fullName}</span>
                            ) : null
                          }
                        </div>
                      </Button.Content>
                    </Button>
                    {util.canManageChallenge(user, idea.ownerChallenge) && removeIdeaFromMerge
                      ? (
                        <Popup
                          on='hover'
                          trigger={(
                            <Button
                              icon='close'
                              onClick={() => removeIdeaFromMerge(idea._id, childIdea._id)}
                            />
                          )}
                          content='Remove this idea from the merge'
                        />

                      ) : null
                    }
                  </Button.Group>
                ))}
              </div>
              {idea.ownerChildren?.length !== idea.children?.length
                ? (
                  <Message info size='tiny'>
                    You are not able to view all ideas in this merge. You may not have permission to view some of the ideas, or they may have been deleted.
                  </Message>
                ) : null
              }
            </ChildIdeaChooser>
            {canJumpToSectionInIdea && (divRef ? divRef.current.scrollTop < 100 : window.scrollY < 100)
              ? (
                <div>
                  <Button
                    // color='purple'
                    icon='arrow circle down'
                    content={`Jump to ${!prevSectionName ? 'previous section' : `section "${prevSectionName}"`}`}
                    onClick={() => onScrollToPrevSection(prevSectionViewed)}
                    className='jump-to-section-button'
                    size='tiny'
                  />
                </div>
              ) : null
            }
          </StickyMergeIdeaHeader>
        </Sticky>
        {(
          user
          && activeChildIdea
          && (
            (
              activeChildIdea?.user === user?._id
              || activeChildIdea?.collaborators?.indexOf(user?._id) !== -1
            )
          )
          && !idea.user === user?._id
        )
          ? (
            <Message info>
              Your idea has been merged with other ideas; You are currently viewing the merge result. You can still view your individual idea <Link to={`/ideas/${activeChildIdea._id}`}>here</Link>.
            </Message>
          ) : null
        }
        <Body
          idea={usingIdea}
          setRef={setRef}
          usingAssessmentTemplate={idea.ideaTemplate}
          isViewingMergedIdea
          {...rest}
        />
        {!removingIdeaFromMerge
          ? (
            <div>
              <div ref={(ref) => setRef && setRef(ref, 'comments')} id='comments'>
                <Divider hidden />
                <Header as='h4'>Comments</Header>
                <IdeaComments
                  idea={idea}
                  forId={idea._id}
                  isViewingMergedIdea
                />
                <Divider hidden />
                <Divider hidden />
              </div>
            </div>
          ) : null
        }
      </div>
    )
  }
  return (
    <Body
      idea={usingIdea}
      setRef={setRef}
      {...rest}
    />
  );
}

const Body = withTranslation()((props) => {
  const {
    t,
    idea,
    challenge,
    loading,
    setRef,
    updateIdea,
    scrollToRequired,
    onSubmit,
    isEditing,
    isPreview,
    hideComments,
    incorrectFields,
    isSubmissible,
    externalChanges,
    isPDF,
    assessment,
    updateAssessment,
    usingAssessmentTemplate,
    submitAssessment,
    isSubmittingAssessment,
    isViewingMergedIdea,
  } = props;
  const user: OpenAPI.GET<'/users/me'> = useAppSelector((state) => state.user);
  const {
    parent, ideaTemplate = {}, attachments = [], commentCount = 0, ownerChallenge = { preventIdeaComments: false, ideaAnonymity: '', stage: '' },
  } = idea;
  const { body = [], isFreeform, disableAttachments } = ideaTemplate;
  const { stage } = ownerChallenge;
  const inputRefs = useRef({});
  const [focusableFields, setFocusableFields] = useState([]);

  const assignTabIndexes = useCallback((sections, refsObj) => {
    const focusableFieldsArray = [];

    // The name field is outside the scope of the sections, so we need to add it to the refs manually
    // We know it's going to be focusable so we add that
    refsObj.current.name = React.createRef();
    focusableFieldsArray.push('name');

    // Add each field in each section to the refs
    // Also track which of those fields we deem to be focusable - e.g. text fields
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        const { id, type } = field;

        if (TextFields.includes(type)) {
          refsObj.current[id] = React.createRef();
          focusableFieldsArray.push(id);
        } else {
          refsObj.current[id] = null;
        }
      });
    });

    if (stage !== 'closed') {
      refsObj.current.submitButton = React.createRef();
      focusableFieldsArray.push('submitButton');
    }

    setFocusableFields(focusableFieldsArray);
  }, [stage]);

  useEffect(() => {
    if (!idea.parent) {
      inputRefs.current = {};
      assignTabIndexes(body, inputRefs);
    }
  }, [idea.parent, body, assignTabIndexes]);

  useEffect(() => {
    // Once we have a list of all focusable fields, we want to focus on the first one
    // This should always be the name field
    if (focusableFields?.length > 0) {
      const firstFocusableFieldId = focusableFields[0];
      const firstFocusableField = inputRefs.current[firstFocusableFieldId];
      if (firstFocusableField?.current?.focus) {
        firstFocusableField.current.focus();
      }
    }
  }, [focusableFields]);

  const handleKeyDown = useCallback((event, fieldId) => {
    if (event.key === 'Tab') {
      event.preventDefault();

      // inputRefs is an object containing all idea fields on the current page
      // The only non-null elements will be valid input elements that we are able to focus on
      // Whenever the user presses tab (and depending on whether they are holding shift), we want to focus on the next/previous input element in the list
      // If the user is tabbing forward, we need to iterate through the objects values until we find the current input element, then focus on the next one
      // If the user is tabbing backwards, we need to iterate through the objects values until we find the current input element, then focus on the previous one
      // In order to know what the next valid focusable element is, we need to create a new object that associates each focusable fieldId with its index in the list
      // We can then use the current fieldId to find its index, and then use the shift key to determine whether we should focus on the next or previous element

      const currentIndex = focusableFields.findIndex((focusableFieldId) => focusableFieldId === fieldId);
      const nextIndex = event.shiftKey
        ? currentIndex - 1
        : currentIndex + 1;

      if (nextIndex >= 0 && nextIndex < focusableFields.length) {
        const newIndexInInputRefs = focusableFields[nextIndex];
        inputRefs.current[newIndexInInputRefs].current.focus();
      }
    }
  }, [focusableFields]);

  const canEdit = util.canEditIdea(user, idea);
  const showAttachments = (!disableAttachments && ((canEdit && isEditing) || (attachments.length > 0)));
  const showComments = ((!isEditing || commentCount) && !ownerChallenge?.preventIdeaComments && !hideComments && idea._id) && !isPDF && (!isViewingMergedIdea || (isViewingMergedIdea && !idea.parent));
  const submitAssessmentContent = useMemo(() => (
    usingAssessmentTemplate
      ? 'Submit assessment for all ideas'
      : 'Submit assessment'
  ), [usingAssessmentTemplate]);

  return (
    <>
      {idea?.ipStatement?.ipStatementEnabled
        ? (
          <IPStatementInfo ipStatement={idea.ipStatement} />
        ) : null
      }

      {ownerChallenge?.ideaAnonymity && canEdit
        ? (
          <Message
            info
            icon='user secret'
            header='You are working anonymously'
            content='Ideas in this challenge have their identity hidden to anyone who is not a challenge administrator. Only you and challenge administrators will be able to see that you created this idea.'
          />
        ) : null
      }

      {idea?.isAssessor
        && (
          <AssessmentClosureInfo ideaTemplate={ideaTemplate} />
        )
      }

      <IdeaAssessmentConflictOfInterestBox
        idea={idea}
        assessment={assessment}
        updateAssessment={updateAssessment}
        noShadow={undefined}
        style={undefined}
        className={undefined}
        isPDF={isPDF}
      />
      {assessment?.conflictOfInterest?.hasConflictOfInterest && idea?.isSubmitted
        ? null
        : (
          <>
            {loading
              && (
                <Loader active />
              )
            }

            <IdeaNameField
              isEditing={isEditing}
              idea={idea}
              challenge={challenge}
              updateIdea={updateIdea}
              // @ts-ignore
              nameRef={inputRefs.current.name}
              handleKeyDown={(event) => handleKeyDown(event, 'name')}
            />

            {!isFreeform
              ? (body.map((section, sectionIndex) => {
                if (!util.shouldShowIdeaSection(idea, section)) return null;
                if (!util.shouldShowIdeaEdiSection(user, idea, section)) return null;
                return (
                  <div style={{ marginBottom: 30 }} key={section.id}>
                    <IdeaBodySection
                      section={section}
                      sectionIndex={sectionIndex}
                      isPreview={isPreview}
                      setRef={setRef}
                      updateIdea={updateIdea}
                      externalChanges={externalChanges}
                      idea={idea}
                      inputRefs={inputRefs}
                      handleKeyDown={handleKeyDown}
                      {...props}
                    />
                  </div>
                );
              }))
              : <FreeformTemplate {...props} />
            }
          </>
        )
      }

      {idea?.isAssessor ? (
        <div
          style={{marginBottom: 30 }}
        >
          {assessment?.isSubmitted
            ? (
              <Label color='teal' content='Assessment submitted' />
            ) : <>
              {assessment?.conflictOfInterest?.hasConflictOfInterest
                ? <Popup
                    trigger={
                      <Button
                        style={{ marginLeft: 3 }}
                        content={submitAssessmentContent}
                        primary
                        onClick={submitAssessment}
                        disabled={idea?.ideaTemplate?.assessmentClosed}
                      />}
                      content='Conflict of interest declared. When you submit this assessment, all assessment scores/notes will be cleared.' />
                : <Button
                    style={{ marginLeft: 3 }}
                    content={submitAssessmentContent}
                    primary
                    onClick={submitAssessment}
                    disabled={idea?.ideaTemplate?.assessmentClosed}
                    loading={isSubmittingAssessment}
                  />
              }
            </>}
        </div>
      ) : null}

      {parent || idea?.isAssessor || idea?.children ? null : (
        <SubmitIdeaButton
          user={user}
          idea={idea}
          updateIdea={updateIdea}
          onSubmit={onSubmit}
          incorrectFields={incorrectFields}
          isSubmissible={isSubmissible}
          scrollToRequired={scrollToRequired}
          trigger={(
            // @ts-ignore
            <MobileSubmitButton
              isEditing={isEditing}
              idea={idea}
              addMargin={!showAttachments && !showComments}
              ownerChallenge={ownerChallenge}
              handleKeyDown={(event) => handleKeyDown(event, 'submitButton')}
              // @ts-ignore
              buttonRef={inputRefs.current.submitButton}
              tabIndex={focusableFields.length}
            />
          )}
          // @ts-ignore
          unsubmitTrigger={<MobileSubmitButton unsubmit idea={idea} isEditing={isEditing} addMargin={!showAttachments && !showComments} ownerChallenge={ownerChallenge} />}
          // @ts-ignore
          submitRef={inputRefs.current.submitButton}
          handleKeyDown={(event) => handleKeyDown(event, 'submitButton')}
        />
      )}

      {showAttachments
        ? (
          <div>
            <div ref={(ref) => setRef && setRef(ref, 'attachments')} id='attachments'>
              <Divider section hidden />
              <IdeaAttachments idea={idea} user={user} isEditing={isEditing} updateIdea={updateIdea} />
            </div>
          </div>
        ) : null
      }
      {showComments
        ? (
          <div>
            <div ref={(ref) => setRef && setRef(ref, 'comments')} id='comments'>
              <Divider hidden />
              <Header as='h4'>{t('generic.comments')}</Header>
              <IdeaComments idea={idea} forId={idea._id} />
            </div>
          </div>
        ) : null
      }
    </>
  );
});

export default BodyContainer;
